/*
 * Copyright (C) 2021 Quartet Health, Inc - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */

import * as React from 'react';
import { values } from 'ramda';
import { captureException } from '@sentry/core';
import { Switch, Route, Redirect } from 'react-router-dom';

import { retryImport } from '@coop/utils/retryImport';

import { trackEvent, Action } from 'src/utils/tracking';
import { Spinner } from '@coop/ole/Spinner';
import { Market, MarketContext } from './config';
import { createMedicaidMarket } from './config/medicaid';

const GenericErrorPage = React.lazy(() =>
  retryImport(
    () =>
      import(
        /* webpackChunkName: 'generic-error-page' */ 'src/components/GenericErrorPage'
      )
  )
);

const NotFound = React.lazy(() =>
  retryImport(
    () => import(/* webpackChunkName: 'not-found' */ 'src/components/NotFound')
  )
);

const MedicaidVertical = React.lazy(() =>
  retryImport(
    () =>
      import(
        /* webpackChunkName: 'medicaid-instance' */ 'src/components/MedicaidVertical'
      )
  )
);

const AffiliateNotInScope = React.lazy(() =>
  retryImport(
    () =>
      import(
        /* webpackChunkName: 'affiliate-not-in-scope' */ 'src/components/AffiliateNotInScope/index'
      )
  )
);

const DigitalIntakeForm = React.lazy(() =>
  retryImport(
    () =>
      import(
        /* webpackChunkName: 'digital-intake-form' */ 'src/components/selfReferralForm/digitalIntakeForm/DigitalIntakeFormMaster'
      )
  )
);

export const MarketPaths = {
  Illinois: '/IL',
  Washington: '/WA',
  Louisiana: '/LA',
  NorthCarolina: '/NC',
  Default: '/US'
};

export const MarketIds = {
  Georgia: 'GA',
  Illinois: 'IL',
  Louisiana: 'LA',
  NorthCarolina: 'NC',
  NewJersey: 'NJ',
  Pennsylvania: 'PA',
  Washington: 'WA',
  Arizona: 'AZ',
  Ohio: 'OH',
  Texas: 'TX',
  Default: 'US'
};

export const AffiliatePaths = {
  Root: '/resources/:state'
};

export const ExperiencePaths = {
  Medicaid: '/medicaid'
};

export const FormPaths = {
  SelfReferral: '/member',
  NonMember: '/nonmember',
  GetStarted: '/getstarted',
  Intake: '/intake',
  Minor: '/minor',
  DigitalIntake: '/digital-intake',
  Ineligible: '/ineligible'
};

export const BasePaths = {
  Root: '/',
  HealthCheck: '/_rtrHealth'
};

export const getBackUrl = () => {
  const currentPath = window.location.pathname.toUpperCase();
  const customerPaths = values(MarketPaths);
  const backUrl = customerPaths.filter((path) => currentPath.includes(path));
  return backUrl.length === 1 ? backUrl[0] : BasePaths.Root;
};

export const trackGenericErrorPageRefresh = () => {
  trackEvent('Error-generic-CTA', Action.ClickThrough);
};

export const track404PageGoBack = () => {
  trackEvent('Error-404-CTA', Action.ClickThrough);
};

export const trackMarketPage404 = () => {
  trackEvent('marketSite-redirect', 'redirect');
};

interface State {
  error: boolean;
  market: Market;
}

export class Routes extends React.Component<{}, State> {
  public state: Readonly<State> = {
    error: false,
    market: createMedicaidMarket()
  };

  public componentDidCatch(error: Error) {
    captureException(error);
    this.setState({
      error: true
    });
  }

  public componentDidMount() {
    const splash = document.getElementById('quartet-splash');

    // eslint-disable-next-line no-unused-expressions
    splash?.parentNode?.removeChild(splash);

    this.setState({
      error: false
    });
  }

  public render() {
    return this.state.error ? (
      <GenericErrorPage trackEvent={trackGenericErrorPageRefresh} />
    ) : (
      <React.Suspense fallback={<Spinner copy="Loading..." />}>
        <MarketContext.Provider value={this.state.market}>
          <Switch>
            {/* Health Check */}
            <Route path={BasePaths.HealthCheck}>
              <p>ok</p>
            </Route>

            {/* quartet.getcare root */}
            <Route
              exact
              path={BasePaths.Root}
              render={() => <DigitalIntakeForm />}
            />

            <Route
              exact
              path={AffiliatePaths.Root}
              component={AffiliateNotInScope}
            />

            <Route
              path={[
                FormPaths.GetStarted,
                FormPaths.Minor,
                ExperiencePaths.Medicaid,
                FormPaths.DigitalIntake,
                FormPaths.Ineligible
              ]}
              component={MedicaidVertical}
            />

            <Route
              path={FormPaths.Intake}
              render={() => <DigitalIntakeForm />}
            />

            <Route
              path={[...values(MarketPaths), ...values(FormPaths)]}
              render={() => {
                trackMarketPage404();
                return (
                  <Redirect
                    to={`${BasePaths.Root}${document.location.search}`}
                  />
                );
              }}
            />

            <Route
              path="*"
              render={() => (
                <NotFound
                  trackEvent={track404PageGoBack}
                  backLink={getBackUrl()}
                />
              )}
            />
          </Switch>
        </MarketContext.Provider>
      </React.Suspense>
    );
  }
}
